import React, { useState } from "react";
import { Popover, InputNumber, Button, Space, message } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  useInventoryServiceGetInventoryStockKey,
  useInventoryServiceQuickUpdateProduct,
} from "api/queries";

interface EditableSellingPriceCellProps {
  productId: number;
  sellingPrice: number;
}

const EditableSellingPriceCell: React.FC<EditableSellingPriceCellProps> = ({
  productId,
  sellingPrice,
}) => {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState<number>(sellingPrice);
  const queryClient = useQueryClient();
  const company = useSelector((state: any) => state.company);

  const { mutate, isPending } = useInventoryServiceQuickUpdateProduct({
    onSuccess: async () => {
      message.success("Selling price updated successfully");
      await queryClient.invalidateQueries({
        queryKey: [useInventoryServiceGetInventoryStockKey],
      });
      setEditing(false);
    },
    onError: (error: any) => {
      message.error(`Error updating selling price: ${error.message}`);
    },
  });

  const handleSave = () => {
    if (price === sellingPrice) {
      message.info("No changes detected.");
      setEditing(false);
      return;
    }
    mutate({
      idProduct: productId,
      requestBody: {
        productPrice: price,
      },
    });
  };

  const popoverContent = (
    <div style={{ width: "250px" }}>
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <InputNumber
          min={0}
          value={price}
          onChange={(value) => setPrice(value ?? 0)}
          // Remove formatter so that no currency symbol is shown in the input field.
          parser={(value) => {
            if (!value) return 0;
            const parsed = parseFloat(value.replace(/(,*)/g, ""));
            return isNaN(parsed) ? 0 : parsed;
          }}
          style={{ width: "100%" }}
        />
        <Space>
          <Button size="small" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={handleSave}
            loading={isPending}
          >
            Save
          </Button>
        </Space>
      </Space>
    </div>
  );

  // To re-enable the popup, change trigger={[]} back to trigger="click"
  return (
    <Popover
      content={popoverContent}
      trigger={[]}
      open={editing}
      onOpenChange={(visible) => setEditing(visible)}
    >
      <div style={{ cursor: "pointer" }}>
        {`${company.currencySymbol} ${(sellingPrice || 0).toFixed(2)}`}
      </div>
    </Popover>
  );
};

export default EditableSellingPriceCell;
